import { Box } from "@mui/material";
import { motion } from "framer-motion";

/**
 * BackgroundEffects
 *
 * Replaces the old particle field / neural SVG / scan lines entirely.
 * Three layers, all calm:
 *   1. Blueprint grid — static, faint, masked toward the center
 *   2. Two soft radial glows — very slow scale/opacity drift (18-22s loops)
 *   3. A single faint diagonal light sweep — barely perceptible
 *
 * Performance: only `transform` and `opacity` are animated (GPU compositable).
 * No filters animate, no SVG path animation, no particle loop.
 * prefers-reduced-motion disables all motion via CSS media query — the glows
 * still render (for visual richness) but become fully static.
 */
export default function BackgroundEffects() {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      {/* Blueprint grid — static */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
          backgroundSize: { xs: "40px 40px", md: "56px 56px" },
          maskImage:
            "radial-gradient(ellipse 70% 60% at 30% 40%, black, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 60% at 30% 40%, black, transparent 75%)",
        }}
      />

      {/* Primary glow — top-left, anchors the headline */}
      <Box
        component={motion.div}
        animate={{ scale: [1, 1.08, 1], opacity: [0.5, 0.7, 0.5] }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut",
          // Respect reduced motion by effectively freezing via CSS below
        }}
        sx={{
          position: "absolute",
          top: "-10%",
          left: "-8%",
          width: { xs: 320, sm: 460, md: 620, xl: 760 },
          height: { xs: 320, sm: 460, md: 620, xl: 760 },
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(30,144,255,0.10) 0%, transparent 70%)",
          filter: "blur(60px)",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      />

      {/* Secondary glow — anchors the visualization on the right */}
      <Box
        component={motion.div}
        animate={{ scale: [1, 1.1, 1], opacity: [0.35, 0.5, 0.35] }}
        transition={{ duration: 24, repeat: Infinity, ease: "easeInOut", delay: -6 }}
        sx={{
          position: "absolute",
          top: "8%",
          right: "-10%",
          width: { xs: 280, sm: 400, md: 520, xl: 640 },
          height: { xs: 280, sm: 400, md: 520, xl: 640 },
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(30,144,255,0.08) 0%, transparent 70%)",
          filter: "blur(70px)",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      />

      {/* Faint bottom anchor glow — keeps the lower section from feeling empty */}
      <Box
        sx={{
          position: "absolute",
          bottom: "-15%",
          left: "30%",
          width: { xs: 360, md: 600 },
          height: { xs: 200, md: 320 },
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse, rgba(30,144,255,0.05) 0%, transparent 70%)",
          filter: "blur(50px)",
        }}
      />

      {/* Single faint diagonal light line — purely decorative, static */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "62%",
          width: "1px",
          height: "100%",
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(30,144,255,0.12) 40%, rgba(30,144,255,0.12) 60%, transparent 100%)",
          display: { xs: "none", md: "block" },
        }}
      />

      {/* Bottom vignette so content below blends to black cleanly */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: { xs: "100px", md: "160px" },
          background: "linear-gradient(to top, #000000 0%, transparent 100%)",
        }}
      />
    </Box>
  );
}